import React, {useEffect} from 'react';
import {Box} from '@mui/material';
import {useCommunicationExplorer} from '../../../../hooks/communicationExplorer/useCommunicationExplorer';
import LinkAndBubble from './LinkAndBubble';
import CommunicationActions from './CommunicationActions';
import '../CommunicationReport.scss';
import ArrToastNotification from '../../../../components/common/arr-toast-notification/ArrToastNotification';
import {ToastType} from '../../../../shared/constants';
import {t} from 'i18next';
import {useSearchParams} from 'react-router-dom';
import {ReportNotificationType} from '../../../../components/reports/communication-explorer/CommunicationExplorer';
import ArrWaitToCompleteModalWrapper from '../../../../components/reports/standard-reports/component/waittocomplete-modal-wrapper/ArrWaitToCompleteModalWrapper';

import {CustomMenuItem} from '../../../../components/common/arr-menu/ArrMenu';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import {setCommunicationExplorerViewApiLoaded} from '../../../../redux/slice/communicationExplorerSlice';

interface IBubblesAndLinks {
  reqData: ReportNotificationType | null;
  showBy: string | number | null;
  svgRef: React.RefObject<SVGSVGElement | null>;
  setCanDownload: React.Dispatch<React.SetStateAction<boolean>>;
  onNodeSelectionChange?: (selectedNodeIds: number[]) => void;
  onLinkSelectionChange?: (selectedLinkIds: string[]) => void;
  onDataLoad?: (data: {node: any[]; link: any[]; exchangeDocs: any[]}) => void;
  onReportDataLoad?: (fullData: any) => void;
  selectedNodeIds?: Set<number>;
  selectedLinkIds?: Set<string>;
  actionMenuItems?: CustomMenuItem[];
  sourceDetails?: {[key: string]: any};
  getDoActionData?: (data: {
    operationType?: string;
    sourceDetails?: {[key: string]: any};
  }) => {selectedList?: object; destinationList?: object};
  getSourceDetails?: (
    sourceDetails: {[key: string]: any},
    forAPI?: string
  ) => {[key: string]: any};
  showToastFromParent?: (config: any) => void;
  onActionPick?: (
    action: CustomMenuItem,
    callBackForDoc?: (docId: string, source: string) => void
  ) => void;
  validateBeforeBulkAction?: () => boolean;
  isMenuDisabled?: boolean;
}

const BubblesAndLinks = ({
  reqData,
  showBy,
  svgRef,
  setCanDownload,
  onNodeSelectionChange,
  onLinkSelectionChange,
  onDataLoad,
  onReportDataLoad,
  selectedNodeIds,
  selectedLinkIds,
  actionMenuItems,
  sourceDetails,
  getDoActionData,
  getSourceDetails,
  showToastFromParent,
  onActionPick,
  validateBeforeBulkAction,
  isMenuDisabled,
}: IBubblesAndLinks) => {
  const dispatch = useDispatch();
  const isCommunicationExplorerViewApiLoaded = useSelector(
    (state: RootState) =>
      state.communicationExplorer.isCommunicationExplorerViewApiLoaded
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    data = null,
    refetch: getCommunicationExplorerData,
    isLoading,
    isError,
  } = useCommunicationExplorer({
    reportId: Number(searchParams.get('ReportID')),
  });

  // Fetch data when the view API is loaded and report ID is present
  useEffect(() => {
    if (
      isCommunicationExplorerViewApiLoaded &&
      Number(searchParams.get('ReportID'))
    ) {
      getCommunicationExplorerData();
    }
    dispatch(setCommunicationExplorerViewApiLoaded(false));
    return () => {
      dispatch(setCommunicationExplorerViewApiLoaded(false));
    };
  }, [isCommunicationExplorerViewApiLoaded]);

  useEffect(() => {
    if (!isLoading && data && !isError) {
      setCanDownload(true);
      // Notify parent when data is loaded
      if (onDataLoad && data) {
        onDataLoad({
          node: data.node || [],
          link: data.link || [],
          exchangeDocs: data.exchangeDocs || [],
        });
      }
      // Pass full response data to parent for report criteria extraction
      if (onReportDataLoad && data) {
        onReportDataLoad(data);
      }
    } else {
      setCanDownload(false);
    }
  }, [data, setCanDownload, isLoading, isError, onDataLoad, onReportDataLoad]);

  useEffect(() => {
    return () => {
      if (data) {
        onNodeSelectionChange?.([]);
        onLinkSelectionChange?.([]);
      }
    };
  }, [data]);

  return (
    <>
      {isError && (
        <ArrToastNotification
          message={t(`reports.communicationExplorer.reportAPIError`)}
          status={[ToastType.Error]}
          onClose={() => {}}
        />
      )}
      {reqData?.notificationId && (
        <ArrWaitToCompleteModalWrapper
          notificationId={reqData.notificationId}
          reportId={reqData.reportId}
          onComplete={(reportId) => {
            setSearchParams((prev) => {
              prev.set('ReportID', String(reportId));
              return prev;
            });
          }}
        />
      )}
      <Box className="criteria-box">
        {data && (
          <>
            <CommunicationActions
              exchangeDocs={data?.exchangeDocs[0]}
              showBy={showBy}
              actionMenuItems={actionMenuItems}
              sourceDetails={sourceDetails}
              getDoActionData={getDoActionData}
              getSourceDetails={getSourceDetails}
              showToastFromParent={showToastFromParent}
              onActionPick={onActionPick}
              validateBeforeBulkAction={validateBeforeBulkAction}
              isMenuDisabled={isMenuDisabled}
            />
            <LinkAndBubble
              svgRef={svgRef}
              data={data}
              onNodeSelectionChange={onNodeSelectionChange}
              onLinkSelectionChange={onLinkSelectionChange}
              selectedNodeIds={selectedNodeIds}
              selectedLinkIds={selectedLinkIds}
            />
          </>
        )}
      </Box>
    </>
  );
};

export default BubblesAndLinks;
